import React from 'react';

import classnames from 'classnames';

import { HrefLink } from '../HrefLink/HrefLink';
import { LinkContent } from '../LinkContent/LinkContent';
import styles from './Link.module.css';

export enum LinkIntent {
  breadcrumbs = 'breadcrumbs',
  lastBreadcrumb = 'lastBreadcrumb',
  primary = 'primary',
  quaternary = 'quaternary',
  quinary = 'quinary',
  secondary = 'secondary',
  tertiary = 'tertiary',
}

export enum LinkColors {
  breadcrumbs = 'var(--midnight-purple)',
  lastBreadcrumb = 'var(--grey-300)',
  primary = 'var(--primary-link-color)',
  quaternary = 'var(--quaternary-link-color)',
  quinary = 'var(--quinary-link-color)',
  secondary = 'var(--secondary-link-color)',
  tertiary = 'var(--teritiary-link-color)',
}

export enum LinkType {
  ExternalHref = 'externalHref',
  Href = 'href',
  Span = 'span',
}

export enum LinkTextTransform {
  Capitalize = 'capitalize',
  Lowercase = 'lowercase',
  Uppercase = 'uppercase',
}

export enum LinkTarget {
  Blank = '_blank',
  Parent = '_parent',
  Self = '_self',
  Top = '_top',
}

export enum LinkIconPosition {
  Left = 'left',
  Right = 'right',
}

export type BaseProps = {
  children: JSX.Element | string;
  className?: string;
  icon?: JSX.Element;
  iconPosition?: LinkIconPosition;
  intent?: LinkIntent;
  isBlock?: boolean;
  style?: React.CSSProperties;
  textTransform?: LinkTextTransform;
  withoutHoverStyles?: boolean;
};

export type RouterLinkProps = BaseProps & {
  onClick?: React.MouseEventHandler;
  target?: LinkTarget;
  to: string | (() => string);
  type: LinkType.Href;
};

export type ExternalLinkProps = BaseProps & {
  onClick?: React.MouseEventHandler;
  target?: LinkTarget;
  to: string;
  type: LinkType.ExternalHref;
};

export type SpanProps = BaseProps & {
  onClick?: React.MouseEventHandler | (() => void);
  type: LinkType.Span;
};

export type Props = RouterLinkProps | ExternalLinkProps | SpanProps;

export const Link = React.memo((props: Props) => {
  const {
    children,
    className,
    intent = LinkIntent.tertiary,
    textTransform,
    icon,
    iconPosition,
    withoutHoverStyles,
    isBlock,
    style,
  } = props;

  const classNames = classnames(
    styles.link,
    styles[intent],
    textTransform ? styles[textTransform] : undefined,
    { [styles.block]: isBlock },
    withoutHoverStyles ? undefined : styles['hover'],
    className,
    { [styles.noWrap]: CONFIG.isHyphenation },
  );

  return props.type === LinkType.ExternalHref
    ? (
      <a
        className={classNames}
        href={props.to}
        target={props.target || LinkTarget.Self}
        onClick={props.onClick}
        style={style}
      >
        <LinkContent
          children={children}
          color={LinkColors[intent]}
          icon={icon}
          iconPosition={iconPosition}
        />
      </a>
    )

    : props.type === LinkType.Href
      ? (
        <HrefLink
          className={classNames}
          to={props.to}
          target={props.target || LinkTarget.Self}
          onClick={props.onClick}
        >
          <LinkContent
            children={children}
            color={LinkColors[intent || LinkIntent.tertiary]}
            iconPosition={iconPosition}
            icon={icon}
          />
        </HrefLink>
      )

      : <span
        className={classnames(classNames, styles.span)}
        onClick={props.onClick}
        style={style}
      >
        <LinkContent
          children={children}
          color={LinkColors[intent || LinkIntent.tertiary]}
          iconPosition={iconPosition}
          icon={icon}
        />
      </span>;
});
